import {
    DEFAULT_CRITERIA_PARAMETER_NAME,
    parseSearchCriteria,
    SearchCriteria,
    SearchCriteriaFilterActions,
    serializeSearchCriteria
} from '@corratech/pylot-filters-and-sort/src/hooks/use-plp-manager'
import Image from '@corratech/pylot-image'
import { ConfigurableProductOptionsValues } from '@pylot-data/pylotschema'
import cn from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { ProductPrice } from '../ProductPrice/ProductPrice'
import s from '@components/common/ProductTile/ProductTile.module.scss'
import { MagePlazaProductBadge } from '@corratech/pylot-mageplaza-badges'
import { getPDPUrl } from '@lib/getPDPUrl'
import { getStoreConfig } from 'config'
import { useAlfakherContext } from 'components/alfakherContext/AlfakherContext'
import { YotpoStars } from '@components/YotpoStars/YotpoStars'
import { ProductStockInfo } from '@components/corra/ProductStockInfo'
import { ButtonLabel } from '@pylot-data/hooks/product/use-product-ui'

export interface ProductTileProps {
    product: any
    imageParam: {
        width?: number
    }
    className?: string
    isPDP?: boolean
    gtmLocation?: string
}
export interface Attribute {
    __typename: string
    code: string
    value_index: number
    label: string
}
export interface SmallImage {
    __typename: string
    url: string
}
export interface VariantProduct {
    __typename: string
    id: number
    small_image: SmallImage
    sku: string
    stock_status: string
}
export interface Variant {
    __typename: string
    attributes: Attribute[]
    product: VariantProduct
}
export const LoaderTile = () => (
    <div className={cn(s['productitem-block'])}>
        <Skeleton height={320} width={320} className={s['product-img']} />
        <Skeleton count={2} />
        <Skeleton circle width={16} count={2} />
    </div>
)
export const FwrdProductTile = ({
    product = {},
    imageParam = {},
    className,
    isPDP = false
}: ProductTileProps): ReactElement | null => {
    const { url_key } = product
    const config = getStoreConfig()
    const isConfig = product.__typename === 'ConfigurableProduct'
    const { query } = useRouter()
    const { setScrollToReview } = useAlfakherContext()

    let search = ''
    if (
        typeof window !== 'undefined' &&
        typeof URLSearchParams !== 'undefined'
    ) {
        const params = new URLSearchParams(new URL(window.location.href).search)
        params.delete('page')
        search = params.toString()
    }
    const [urlSearchCriteria, setUrlSearchCriteria] = useState(search)
    const productUrl = getPDPUrl(url_key, isConfig ? urlSearchCriteria : '')
    const [selectedOption, setSelectedOption] = useState<SearchCriteria | null>(
        null
    )
    // TODO: make dynamic?
    const swatchAttr = 'color'
    const colorsImagesMap = new Map()
    const fullImageUrl = useCallback(
        (imageUrl?: string): string => {
            const widthString = imageParam.width
                ? `&width=${imageParam.width}`
                : ''
            return `${imageUrl}?auto=webp&format=pjpg&crop=1:1${widthString}`
        },
        [imageParam.width]
    )
    const [image, setImage] = useState(fullImageUrl(product.small_image?.url))
    // During the changes in URL - convert new filters to product url
    useEffect(() => {
        if (!selectedOption) {
            setUrlSearchCriteria(search)
        }
        if (selectedOption) {
            onVariantChange({
                label: selectedOption.attribute_code,
                value_index: +selectedOption.filter_value
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        query,
        search,
        selectedOption?.attribute_code,
        selectedOption?.filter_value,
        urlSearchCriteria
    ])
    useEffect(() => {
        if (product.small_image?.url) {
            setImage(fullImageUrl(product.small_image?.url))
        }
    }, [fullImageUrl, product.small_image?.url])
    if (isConfig && product.variants) {
        product.variants.map((variant: Variant) => {
            const colorVariant = variant.attributes.find(
                (variant: { code: string }) => variant?.code === swatchAttr
            )
            const img = variant.product.small_image
                ? variant.product.small_image.url
                : false
            if (colorVariant && img) {
                colorsImagesMap.set(colorVariant.label, fullImageUrl(img))
            }
        })
    }
    const onVariantChange = ({
        label,
        value_index
    }: Partial<ConfigurableProductOptionsValues>) => {
        const value = value_index!.toString()
        const img = colorsImagesMap.get(label)
        if (img) {
            // setSelectedLabel(label!)
            setImage(img)
        }
        const searchCriteria = isPDP
            ? []
            : parseSearchCriteria(
                  query[DEFAULT_CRITERIA_PARAMETER_NAME] as string
              )
        const idxOfChangedAttribute = searchCriteria.findIndex(
            (sAttr) => sAttr.attribute_code === swatchAttr
        )
        let newOption: SearchCriteria
        if (idxOfChangedAttribute !== -1) {
            searchCriteria[idxOfChangedAttribute].filter_value = value
            newOption = searchCriteria[idxOfChangedAttribute]
        } else {
            newOption = {
                attribute_code: swatchAttr,
                filter_action: SearchCriteriaFilterActions.EQ,
                filter_value: value
            }
            searchCriteria.push(newOption)
        }
        const newSearchCriteriaString = `?${DEFAULT_CRITERIA_PARAMETER_NAME}=${serializeSearchCriteria(
            searchCriteria
        )}`
        setUrlSearchCriteria(newSearchCriteriaString)
        setSelectedOption(newOption)
    }

    if (!product) return null

    const onProductClick = () => {
        if (!query?.url_path?.length) return
        const prevUrlPaths = localStorage.getItem('plpUrlPath')
        let plpUrlPaths: { [name: string]: string } = {}
        try {
            if (prevUrlPaths) plpUrlPaths = JSON.parse(prevUrlPaths)
        } catch (error) {
            localStorage.removeItem('plpUrlPath')
        }
        const paths =
            (query.url_path as string[])?.findIndex((v: string) =>
                v.includes('query')
            ) > 0
                ? query?.url_path?.slice(0, -1)
                : query?.url_path
        const urlPath = paths as []
        const keys = Object.keys(plpUrlPaths)
        if (keys.length > 19) delete plpUrlPaths[keys[0]]
        plpUrlPaths[product?.url_key] = urlPath.join('/')
        localStorage.setItem('plpUrlPath', JSON.stringify(plpUrlPaths))
    }

    return (
        <Link href={productUrl}>
            <div
                className={s['product-item-container']}
                aria-hidden="true"
                onClick={onProductClick}
                onKeyDown={onProductClick}
            >
                <div className={cn(s['productitem-block'], className)}>
                    <div className={s['productimage-block']}>
                        <div
                            className={cn(
                                s['productimg-link'],
                                'product-card-image-swipe'
                            )}
                            aria-label={product.name}
                        >
                            <a href={productUrl} className={s['product-link']}>
                                <div className={s['badges-container']}>
                                    {product.mp_label_data && (
                                        <MagePlazaProductBadge
                                            labelData={product.mp_label_data}
                                            showAllCustom
                                        />
                                    )}
                                </div>
                                <Image
                                    src={image}
                                    alt={
                                        product.name
                                            ? product.name
                                            : 'placeholder'
                                    }
                                    width={320}
                                    height={320}
                                    className={s['product-img']}
                                />
                            </a>
                        </div>
                    </div>
                    <button
                        className={s['plp-rating']}
                        onClick={() => setScrollToReview()}
                    >
                        <YotpoStars
                            productId={product?.id}
                            yotoApiKey={config.base.yotpo.api_key}
                        />
                    </button>
                    <div className={s['product-details']}>
                        <a className={s['product-link']} href={productUrl}>
                            {product.product_eyebrow_badge_text && (
                                <div className={cn(s['product-eyebrow'])}>
                                    <span
                                        className={cn(
                                            'default-badge',
                                            product?.product_eyebrow_badge_text &&
                                                String(
                                                    product?.product_eyebrow_badge_text
                                                ).toLowerCase()
                                        )}
                                    >
                                        {product.product_eyebrow_badge_text}
                                    </span>
                                </div>
                            )}
                            <strong
                                className={cn(
                                    s['product-brand'],
                                    'product-brand-name'
                                )}
                            >
                                {product.brand_text ? product.brand_text : ''}
                            </strong>
                            <div
                                className={cn(
                                    s['product-name'],
                                    'product-name'
                                )}
                                title={product.name}
                            >
                                {product.name}
                            </div>
                            {product.product_type_label && (
                                <div
                                    className={cn(
                                        s['product-type'],
                                        'product-type'
                                    )}
                                >
                                    {product.product_type_label}
                                </div>
                            )}
                            {product.model_no && (
                                <div className={cn(s['model-no'], 'model-no')}>
                                    {product.model_no}
                                </div>
                            )}
                        </a>

                        <ProductPrice
                            className={String(product.__typename).toLowerCase()}
                            priceRange={product.price_range}
                            productType={product.__typename}
                        />
                    </div>
                    {product?.stock_status === 'OUT_OF_STOCK' && (
                        <div className={s['stock-status-plp']}>
                            <ProductStockInfo
                                stockStatus={ButtonLabel.OUT_OF_STOCK}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Link>
    )
}
export const ProductItem = React.memo(
    (props: ProductTileProps) => <FwrdProductTile {...props} />,
    (prevProps, newProps) =>
        JSON.stringify(prevProps) === JSON.stringify(newProps)
)
ProductItem.displayName = 'MemoProductItem'
